import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import MDEditor from '@uiw/react-md-editor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const BeritaDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  // Remove token requirement - now public access
  const [berita, setBerita] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchBeritaDetail();
    }
  }, [id]);

  const fetchBeritaDetail = async () => {
    try {
      const response = await axios.get(`${API}/berita/${id}`);
      setBerita(response.data);
    } catch (error) {
      console.error('Error fetching berita detail:', error);
      toast.error('Berita tidak ditemukan');
      navigate('/berita');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-ltb-bright"></div>
          <p className="mt-2 text-gray-600">Memuat berita...</p>
        </div>
      </div>
    );
  }

  if (!berita) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 text-lg">Berita tidak ditemukan</p>
          <button
            onClick={() => navigate('/berita')}
            className="mt-4 text-ltb-bright hover:text-ltb-medium"
          >
            ← Kembali ke daftar berita
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white shadow-md py-3 sm:py-4 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <button
              onClick={() => navigate('/berita')}
              className="text-ltb-bright hover:text-ltb-medium"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-lg sm:text-xl font-bold text-gray-800">Detail Berita</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Featured Image */}
          {berita.gambar_url && (
            <div className="w-full h-56 sm:h-64 md:h-80">
              <img 
                src={berita.gambar_url} 
                alt={berita.judul}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Article Content */}
          <div className="p-4 sm:p-6 md:p-8">
            {/* Title */}
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              {berita.judul}
            </h1>

            {/* Meta Info */}
            <div className="border-b pb-4 mb-6 space-y-3">
              {/* Date */}
              <div className="flex items-center">
                <span className="text-sm text-gray-600">
                  📅 {formatDate(berita.tanggal_publish)}
                </span>
              </div>
              
              {/* Tags */}
              {berita.t_berita_tags && berita.t_berita_tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {berita.t_berita_tags.map((tag) => (
                    <span 
                      key={tag.tag_name} 
                      className="bg-blue-100 text-blue-600 px-3 py-1.5 rounded-full text-xs font-medium"
                    >
                      #{tag.tag_name}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Article Body */}
            <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none">
              <MDEditor.Markdown 
                source={berita.isi_berita} 
                style={{ 
                  backgroundColor: 'transparent',
                  color: 'inherit'
                }}
              />
            </div>

            {/* Share Section (Optional) */}
            <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3">Bagikan Berita</h3>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success('Link berhasil disalin');
                  }}
                  className="bg-gray-100 text-gray-700 px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-200 transition-all flex items-center text-sm sm:text-base"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy Link
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Back to List */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/berita')}
            className="bg-ltb-bright text-white px-6 py-3 rounded-lg hover:bg-ltb-medium transition-all"
          >
            ← Kembali ke Daftar Berita
          </button>
        </div>
      </article>
    </div>
  );
};

export default BeritaDetailPage;